/**
 * API ⇄ screen contract for the scoped console.
 *
 * These shapes mirror the backend's `src/web/src/lib/adapters.ts` (ADR-0015 §4:
 * "promote the contract to a shared package `@bicport/contract`"). Until that
 * package is extracted, this file is the single source of truth the frontend
 * consumes; it must stay in sync with `adapters.ts`.
 */

// ─── Portfolio (GET /api/portfolio) ──────────────────────────────────────────
export type SbomStatus = 'complete' | 'indeterminate' | 'pending' | 'failed' | 'none';

export interface VersionRow {
  id: string;
  label: string;
  placedOnMarket: boolean;
  sbomStatus: SbomStatus;
  componentCount: number;
  sbomQuality: number | null;
  /** Scoped-console addition: Release / Non-release (excluded from Art.14). */
  release?: boolean;
  /** CRA release status (FAQ/Recital 37): only 'release' is Art.14-tracked. */
  releaseStatus?: 'release' | 'rc' | 'beta' | 'alpha' | 'internal';
  /** Real per-version counts (P4 read model). */
  openFindings?: number;
  art14Open?: number;
}

export interface ProductRow {
  id: string;
  name: string;
  description: string | null;
  ownerUserRef: string | null;
  /** Markets include non-EU commercial markets like CH (REVIEW-FIXES C-7). */
  markets: string[];
  autoApproveFp: boolean;
  /** Product lifecycle phase (scoped console). */
  phase: 'not_released' | 'active' | 'eol' | 'retired';
  craClass: 'default' | 'important_1' | 'important_2' | 'critical';
  versions: VersionRow[];
  /** Denormalised badge counts for the list. */
  openFindings: number;
  art14Open: number;
}

export interface Portfolio {
  products: ProductRow[];
}

// ─── Warnings / findings (GET /api/warnings) ─────────────────────────────────
export type StatusKind =
  | 'critical' | 'high' | 'medium' | 'low'
  | 'indeterminate' | 'safe' | 'uncertain' | 'dismissed';

export interface WarningRow {
  findingId: string;
  cveId: string | null;
  component: string;
  componentVersion: string | null;
  severity: string | null;
  cvss: number | null;
  epss: number | null;
  isKev: boolean;
  eol?: boolean;
  applicability: 'applicable' | 'not_applicable' | 'uncertain';
  productName: string | null;
  versionLabel: string | null;
  /** Scoped-console additions (P3): stable ids for version-scoped filtering. */
  versionId?: string;
  productId?: string;
  sources: string[];
  vexStatus: string | null;
}

// ─── Tasks (GET /api/tasks) — the Today feed ─────────────────────────────────
export interface TaskRow {
  id: string;
  title: string;
  source: 'warning' | 'art14_event' | 'manual';
  status: 'todo' | 'in_progress' | 'done';
  assigneeUserRef: string | null;
  productName: string | null;
  cveId: string | null;
  due: string | null;
  hot?: boolean;
  nav: string;
}

// ─── Art.14 events / incidents (GET /api/art14) ──────────────────────────────
export type IncidentKind = 'vulnerability' | 'incident';
export type Art14Status = 'open' | 'notified' | 'overdue';
export type ReportStageKey = '24h' | '72h' | '14d' | '1m' | 'complete';

export interface Art14Row {
  id: string;
  kind: IncidentKind;
  status: Art14Status;
  milestone: ReportStageKey;
  cveId: string;
  cvss: number | null;
  epss: number | null;
  isKev: boolean;
  component: string | null;
  versionLabel: string;
  productName: string;
  assigneeUserRef: string | null;
  /** The product this incident belongs to — for the breadcrumb link. */
  productId?: string;
  /** aware-since / T0 — drives every deadline (REVIEW-FIXES C-1). */
  detectedAt: string;
  /** Q16 v21 — set when the corrective measure ships; anchors the 14d clock. */
  fixAvailableAt: string | null;
  markets: string[];
  acknowledgedAt: string | null;
  notifiedAt: string | null;
  /** SRP submission stamps (drive derived action-log rows). */
  followUpSubmittedAt?: string | null;
  finalReportSubmittedAt?: string | null;
  usersInformedAt: string | null; // Art.14(8) — REVIEW-FIXES C-4
  /** User-entered Q16 values keyed by field id ({"v16": "..."}). */
  q16Overrides?: Record<string, string>;
  /** The active VEX recorded for this event's (version, vuln), if any. */
  vexStatus?: string | null;
  vexJustification?: string | null;
  vexJustificationCode?: string | null;
  vexImpact?: string | null;
  vexRemediation?: string | null;
  /** Removal state — one is set when the incident is archived / soft-deleted / not-applicable (Archived view). */
  archivedAt?: string | null;
  deletedAt?: string | null;
  notApplicableAt?: string | null;
  reportStatus: ReportStatus;
}

// ─── Knowledge-base catalog (GET /api/kb) ────────────────────────────────────
export interface KbAffected {
  product: string;
  versions: string;
}
export interface KbVuln {
  id: string;
  cveId: string;
  severity: string | null;
  cvss: number | null;
  epss: number | null;
  isKev: boolean;
  affected: KbAffected[];
  affectedMore: number;
  description: string | null;
  /** 'kev' = local CISA KEV catalog; 'osv' = live OSV lookup. */
  source: 'kev' | 'osv';
}
export interface KbPage {
  rows: KbVuln[];
  total: number;
  page: number;
  pageSize: number;
  /** How the result set was produced. */
  mode: 'catalog' | 'cve' | 'search';
  /** A live OSV call failed and results fell back to the local catalog. */
  degraded?: boolean;
}

// ─── Org profile (GET /api/org-profile) ──────────────────────────────────────
export interface OrgProfile {
  legalName?: string;
  establishment?: string;
  spoc?: string;
  csirt?: string;
  srpRegistered?: boolean;
  defaultManagerRef?: string;
  defaultMarkets?: string[];
}

// ─── People directory (GET /api/members) ─────────────────────────────────────
export interface Member {
  id: string;
  name: string;
  role: string;
}

// ─── Report status (U-1: New · Triaged · Reviewed · Sent; Pending review set by Generate) ──
export type ReportStatus = 'New' | 'Triaged' | 'Pending review' | 'Reviewed' | 'Sent';

// ─── Severity chip helper (ported from adapters.findingStatusKind) ───────────
export function findingStatusKind(w: {
  severity: string | null;
  cvss: number | null;
  applicability: string;
  eol?: boolean;
}): StatusKind {
  if (w.applicability === 'not_applicable') return 'dismissed';
  if (w.applicability === 'uncertain') return 'uncertain';
  const s = (w.severity ?? '').toLowerCase();
  if (s === 'critical' || (w.cvss ?? 0) >= 9) return 'critical';
  if (s === 'high' || (w.cvss ?? 0) >= 7) return 'high';
  if (s === 'medium' || (w.cvss ?? 0) >= 4) return 'medium';
  return 'low';
}

export function shortUtc(iso: string): string {
  return iso.slice(0, 16).replace('T', ' ') + ' UTC';
}
